import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useMutation } from '@tanstack/react-query';
import { submitConfirmDelivery } from '@services/my-orders-sales-service';
import useTimelineQuery from '@hooks/orders-sales/use-timeline-query';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import { TimelineConfirmDeliveryAction } from '@interfaces/models/timeline';
import styles from '../popin.module.scss';
import { CancelButtonView, getErrorString, OkButtonView, OkView, PopinView } from './common';

export interface ConfirmDeliveryProps {
  closePopin: () => void;
  product: Product;
  order: Order | null;
  confirmDeliveryDetails: TimelineConfirmDeliveryAction;
  title: string;
  body: string;
  cancelButtonText: string;
  confirmButtonText: string;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const ConfirmDeliveryCommon: React.FC<ConfirmDeliveryProps> = ({
  closePopin,
  confirmDeliveryDetails,
  product,
  order,
  title,
  body,
  cancelButtonText,
  confirmButtonText,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('orders-sales');
  const { refetchTimeline } = useTimelineQuery(product.productId, order ? order.orderId : null, false);

  const { isSuccess, isLoading, isError, data, mutate } = useMutation({
    mutationFn: () => submitConfirmDelivery(confirmDeliveryDetails.tmsShipmentId, confirmDeliveryDetails.deliveryType),
  });

  const hasError = isError || (data != null && data.kind !== 'Success');
  const hasSuccess = isSuccess && data != null && data.kind === 'Success';
  const errorMessage = data ? getErrorString(t, data) : null;

  if (hasSuccess) {
    return (
      <PopinView
        closePopin={() => {
          refetchTimeline();
          closePopin();
        }}
        title={title}
      >
        <OkView message={t('MY_ACCOUNT.ORDERS_SALES.COMPLETED')} />
      </PopinView>
    );
  }

  return (
    <PopinView
      closePopin={closePopin}
      title={title}
    >
      <div className={clsx('vc-modal__content', styles.ordersSales__popin__content)}>
        <div className="intro">{body}</div>
        {hasError && (
          <div className={styles.ordersSales__popin__errorText}>
            {errorMessage || t('MY_ACCOUNT.ORDERS_SALES.ERROR.API')}
          </div>
        )}
        <div className={styles.ordersSales__popin__buttons}>
          <CancelButtonView
            isSubmitting={isLoading}
            onClick={() => {
              if (onCancel) {
                onCancel();
              }
              closePopin();
            }}
          >
            {cancelButtonText}
          </CancelButtonView>
          <OkButtonView
            isSubmitting={isLoading}
            onClick={() => {
              if (onConfirm) {
                onConfirm();
              }
              mutate();
            }}
          >
            {confirmButtonText}
          </OkButtonView>
        </div>
      </div>
    </PopinView>
  );
};

export default ConfirmDeliveryCommon;
